<script lang="ts">
    import type { Writable } from 'svelte/store'
    import { fade } from 'svelte/transition'

    import { isEqual } from 'lodash-es'

    import { gettext as _ } from 'hydria/services/gettext'
    import { lg } from 'hydria/services/language'
    import { notify } from 'hydria/services/notifications'
    import { asAny } from 'hydria/svelte_helpers'
    import { wbr } from 'hydria/utils/strings'

    import { algorithmIndex, algorithms, digestIndex } from 'wdom/services/dnssec'
    import { subaccount } from 'wdom/subaccount'
    import type { DnssecKey, DsEntry, PubStatus, Zone, ZoneDomainRelation, ZoneStatus } from 'wdom/zone/zone'

    import Date from 'hydria/components/Date.svelte'
    import HelpButton from 'wdom/components/HelpButton.svelte'

    interface Props {
        status: Writable<'loading' | 'ready' | 'not-found' | 'error'>
        zone: Writable<Zone | undefined>
        busy: Writable<boolean | string>
        zoneEdit: Writable<Zone | undefined>
        zoneStatus: Writable<ZoneStatus | null>
        zoneDomainRelation: Writable<ZoneDomainRelation>
        pubStatus: Writable<PubStatus>
        dnssecKeys: Writable<{ zsk: DnssecKey[]; ksk: DnssecKey[] }>
        rolloverAlgorithms: Writable<{ zsk: number[]; ksk: number[] }>
        dnssecActiveKeyCount: Writable<{ zsk: number; ksk: number }>
        dsRecordsHasActive: Writable<boolean>
        dsRecords: Writable<{ ds: DsEntry; key: DnssecKey }[]>
        allowedAlgorithms: Set<number>
        class?: string | null
        generateNewKey: (type: 'zsk' | 'ksk', algId: number) => void
        expireKey: (key: DnssecKey) => void
        unexpireKey: (key: DnssecKey) => void
        rotateKey: (type: 'zsk' | 'ksk') => void
    }

    let { status, zone, busy, zoneEdit, zoneStatus, zoneDomainRelation: zdr, pubStatus, dnssecKeys, rolloverAlgorithms, dnssecActiveKeyCount, dsRecordsHasActive, dsRecords, allowedAlgorithms, class: classes = null, generateNewKey, expireKey, unexpireKey, rotateKey }: Props = $props()

    const KTYPES: Array<'zsk' | 'ksk'> = ['zsk', 'ksk']

    function keyRollover(type: 'zsk' | 'ksk') {}

    let generateKeyModal: 'zsk' | 'ksk' | null = $state(null)
    let newKeyAlgId: number | null = $state(null)

    function generateKey() {
        if (!generateKeyModal || newKeyAlgId == null) return

        generateNewKey(generateKeyModal, newKeyAlgId)
        generateKeyModal = null
        newKeyAlgId = null
    }

    let keyToExpire: DnssecKey | null = $state(null)
    function doExpireKey() {
        if (!keyToExpire) return

        expireKey(keyToExpire)
        keyToExpire = null
    }

    let keyToUnexpire: DnssecKey | null = $state(null)
    function doUnexpireKey() {
        if (!keyToUnexpire) return

        unexpireKey(keyToUnexpire)
        keyToUnexpire = null
    }

    let keyTypeToRotate: 'zsk' | 'ksk' | null = $state(null)
    function doRotateKey() {
        if (!keyTypeToRotate) return

        rotateKey(keyTypeToRotate)
        keyTypeToRotate = null
    }

    let copyTimer: ReturnType<typeof setTimeout> | undefined = undefined
    let copied: boolean | null = $state(null)
    $effect(() => {
        if (copied !== null) {
            if (copyTimer) clearTimeout(copyTimer)
            copyTimer = setTimeout(() => {
                copied = null
                copyTimer = undefined
            }, 3000)
        } else if (copyTimer) {
            clearTimeout(copyTimer)
            copyTimer = undefined
        }
    })

    function copyToClipboard() {
        copied = null
        const finalTextPieces: Array<string> = []
        for (const dsr of $dsRecords) {
            if (dsr.key._status != 'active') continue
            // prettier-ignore
            finalTextPieces.push([
                `Date      : ${dsr.key.ctime.toFormat('yyyy-MM-dd H:mm:ss')}`,
                `Key tag   : ${dsr.key.key_tag}`,
                `Algorithm : ${dsr.key.alg} – ${$algorithmIndex[dsr.key.alg].tag}`,
                `Digest    : ${dsr.ds.digest_type} – ${$digestIndex[dsr.ds.digest_type].name}: ${dsr.ds.digest}`
            ].join('\n'))
        }
        navigator.clipboard.writeText(finalTextPieces.join('\n\n')).then(
            () => {
                notify({
                    type: 'success',
                    title: $_('DS Records'),
                    text: $_('The DS records have been copied to the clipboard.'),
                })
                copied = true
            },
            () => {
                notify({
                    type: 'danger',
                    title: $_('DS Records'),
                    text: $_('Could not copy the text.'),
                })
                copied = false
            },
        )
    }

    $effect(() => {
        if ((keyTypeToRotate || keyToExpire || keyToUnexpire || generateKeyModal) && !document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.add('modal-open')
        } else if (!(keyTypeToRotate || keyToExpire || keyToUnexpire || generateKeyModal) && document.documentElement.classList.contains('modal-open')) {
            document.documentElement.classList.remove('modal-open')
        }
    })
</script>

<svelte:window
    onkeydown={(e) => {
        if (e.key == 'Escape') {
            if (generateKeyModal) {
                generateKeyModal = null
                newKeyAlgId = null
            }
            if (keyToExpire) keyToExpire = null
            if (keyToUnexpire) keyToUnexpire = null
            if (keyTypeToRotate) keyTypeToRotate = null
        }
        // else if (e.shiftKey && e.key == 'Enter') save()
    }}
/>

<!-- NOT translated on purpose: this is very technical, and nobody understands translated terms. -->

{#if $status == 'ready' && $pubStatus.published && $zdr.domain_has_dnssec_support}
    <div>
        <div class="standalone-title text-primary d-flex justify-content-between"><span>{$_('DNSSEC')}</span> <HelpButton tag={'zone-dnssec'} /></div>
        <div class="card dnssec-card{classes ? ` ${classes}` : ''}">
            <div class="card-body">
                {#each KTYPES as ktype}
                    <div class="dnssec-sub-title">
                        <div class="text">
                            {#if ktype == 'zsk'}Zone-Signing Keys (ZSK){:else}Key-Signing Keys (KSK){/if}
                        </div>
                        <div class="actions">
                            {#if $subaccount.zone}
                                <div class="d-flex gap-2">
                                    <button class="btn btn-outline-light btn-sm" type="button" onclick={() => (keyTypeToRotate = ktype)} disabled={!!$busy || !$rolloverAlgorithms[ktype].length}>{$_('Initiate a key rollover')}</button>
                                    <button class="btn btn-outline-light btn-sm" type="button" onclick={() => (generateKeyModal = ktype)} disabled={!!$busy}>{$_('Generate a new key')}</button>
                                </div>
                            {/if}
                        </div>
                        <HelpButton tag="zone-dnssec#{ktype}" />
                    </div>
                    <div class="dnssec-key-table {ktype}-list">
                        <div class="dkt-row head">
                            <div class="dkt-status">{$_('Status')}</div>
                            <div class="dkt-date">{$_('Date')}</div>
                            <div class="dkt-key-tag">Key tag</div>
                            <div class="dkt-alg">Algorithm</div>
                            {#if ktype == 'ksk'}
                                <div class="dkt-digest-types">Digest types</div>
                            {/if}
                            <div class="dkt-date">{$_('Expiration')}</div>
                            <div class="dkt-actions"></div>
                        </div>
                        <div class="dkt-entries">
                            {#if !$dnssecKeys[ktype].length}
                                <div class="dkt-no-entries"><div>{$_('There are no keys')}</div></div>
                            {:else}
                                {#each $dnssecKeys[ktype] as key}
                                    <div class="dkt-row dks-key-row key-status-{key._status}">
                                        <div class="dkt-status">
                                            <span class="s-active"><span class="label label-primary">{$_('Active(feminin)')}</span></span>
                                            <span class="s-expiring"><span class="label label-warning">{$_('Expiring')}</span></span>
                                            <span class="s-expired"><span class="label label-default">{$_('Expired')}</span></span>
                                            <span class="s-deleted"><span class="label label-default">{$_('Deleted')}</span></span>
                                        </div>
                                        <div class="dkt-date"><Date value={key.ctime} format="h-table-s" /></div>
                                        <div class="dkt-key-tag">{key.key_tag}</div>
                                        <div class="dkt-alg"><span>{key.alg} –</span><span>{$algorithmIndex[key.alg].text}</span></div>
                                        {#if ktype == 'ksk'}
                                            <div class="dkt-digest-types">
                                                {#each $dsRecords as dsr}<!-- FIXME: Not efficient -->
                                                    {#if dsr.key == key && (dsr.key._status == 'active' || dsr.key._status == 'expiring')}
                                                        <span><span>{dsr.ds.digest_type} – {$digestIndex[dsr.ds.digest_type].name}</span></span>
                                                    {/if}
                                                {/each}
                                            </div>
                                        {/if}
                                        <div class="dkt-date"><Date value={key.expire_on} format="h-table-s" /></div>
                                        <div class="dkt-actions">
                                            {#if $subaccount.zone}
                                                {#if key._status == 'active'}
                                                    <button type="button" class="btn btn-sm btn-outline-secondary" disabled={!!$busy || !$zoneStatus || !isEqual($zone, $zoneEdit) || !$zoneStatus.published} onclick={() => (keyToExpire = key)}>{$_('Expire')}</button>
                                                {:else if key._status == 'expiring' || key._status == 'expired'}
                                                    <button type="button" class="btn btn-sm btn-outline-secondary" disabled={!!$busy || !$zoneStatus || !isEqual($zone, $zoneEdit) || !$zoneStatus.published} onclick={() => (keyToUnexpire = key)}>{$_('Bring back')}</button>
                                                {/if}
                                            {/if}
                                        </div>
                                    </div>
                                {/each}
                            {/if}
                        </div>
                    </div>
                    {#if $dnssecActiveKeyCount[ktype] > 1}
                        <div class="dnssec-block-desc simple">
                            <p class="text-danger">
                                {#if ktype == 'zsk'}{$_('There should only be one active key. More than one causes bigger zone files, biger network packets, and slows down the resolving.')}
                                {:else}{$_('There should only be one active key. More than one causes bigger network packets, and slows down the resolving.')}
                                {/if}
                            </p>
                        </div>
                    {/if}
                {/each}
                <div class="dnssec-sub-title">
                    <div class="text">{$_('DS Records')}</div>
                    <div class="actions">
                        <div class="d-flex gap-2">
                            {#if copied !== null}
                                <button class="btn btn-sm" type="button" disabled={true} class:btn-success={copied === true} class:btn-danger={copied === false}>
                                    {copied ? $_('Copied!') : $_('Could not copy to clipboard')}
                                </button>
                            {/if}
                            <button class="btn btn-outline-light btn-sm" type="button" onclick={() => copyToClipboard()}>{$_('Copy active to clipboard')}</button>
                        </div>
                    </div>
                    <HelpButton tag="zone-dnssec#ds-records" />
                </div>
                <div class="dnssec-key-table ds-list">
                    <div class="dkt-row head">
                        <div class="dkt-status">{$_('Status')}</div>
                        <div class="dkt-date">{$_('Date')}</div>
                        <div class="dkt-key-tag">Key tag</div>
                        <div class="dkt-alg">Algorithm</div>
                        <div class="dkt-digest-type">Digest type</div>
                        <div class="dkt-digest">Digest</div>
                    </div>
                    <div class="dkt-entries">
                        {#if !$dsRecords.length}
                            <div class="dkt-no-entries">
                                <div>{$_('There are no signatures')}</div>
                            </div>
                        {:else}
                            {#each $dsRecords as dsr}
                                <div class="dkt-row dkt-ds-row key-status-{dsr.key._status}">
                                    <div class="dkt-status">
                                        <span class="s-active"><span class="label label-primary">{$_('Active(feminin)')}</span></span>
                                        <span class="s-expiring"><span class="label label-warning">{$_('Expiring')}</span></span>
                                        <span class="s-expired"><span class="label label-default">{$_('Expired')}</span></span>
                                        <span class="s-deleted"><span class="label label-default">{$_('Deleted')}</span></span>
                                    </div>
                                    <div class="dkt-date"><Date value={dsr.key.ctime} format="h-table-s" /></div>
                                    <div class="dkt-key-tag">{dsr.key.key_tag}</div>
                                    <div class="dkt-alg"><span>{dsr.ds.alg} –</span><span>{$algorithmIndex[dsr.ds.alg].text}</span></div>
                                    <div class="dkt-digest-type"><span>{dsr.ds.digest_type} –</span><span>{$digestIndex[dsr.ds.digest_type].name}</span></div>
                                    <div class="dkt-digest">{@html wbr(dsr.ds.digest, [16, 10], undefined, undefined, true)}</div>
                                </div>
                            {/each}
                        {/if}
                    </div>
                </div>
                {#if $dsRecordsHasActive && !$zdr.domain_has_ds_records}
                    <div class="dnssec-block-desc simple">
                        <p class="text-danger">{$_('DNSSEC is disabled on the domain. To enable, configure the DS records on the domain.')}</p>
                    </div>
                {/if}
                {#if !$zdr.ds_consistents && $zdr.domain_has_ds_records}
                    <div class="dnssec-block-desc simple">
                        <p class="text-danger">{$_('The DS records on the domain do not match those above.')}</p>
                    </div>
                {/if}
                <!---->
            </div>
        </div>
    </div>
{/if}

{#if $zoneStatus && keyTypeToRotate}
    <div class="modal" role="dialog" transition:fade={{ duration: 250 }}>
        <div class="modal-dialog modal-lg">
            <div class="modal-content">
                <div class="modal-header text-bg-primary"><div>{$_('Rollover of DNSSEC keys')}</div></div>
                <div class="modal-body">
                    <p>{$_('The active keys will be set to become expired after some time.')}</p>
                    <p>{$_('New keys matching the same algorithms will be generated.')}</p>
                    <p>{$_('The zone will be signed with both the expiring and the new keys. Once the keys have expired, the zone will be re-signed using only the new keys.')}</p>
                    {#if keyTypeToRotate == 'ksk'}
                        <p>
                            {#if $zoneStatus.ds_domain_linked}
                                {$_('The domain will then be automatically updated with the signature of the new keys.')}
                            {:else}
                                {$_('You will need to update DS records on the domain to only keep the records matching the currently active key, excluding the expiring keys.')}
                            {/if}
                        </p>
                    {/if}
                    <p>{$_('You will need to update DS records on the domain to only keep the records matching the currently active key, excluding the expiring keys.')}</p>
                </div>
                <div class="modal-footer text-bg-light">
                    <button class="btn btn-outline-secondary" type="button" onclick={() => (keyTypeToRotate = null)}>{$_('Cancel')}</button>
                    <button class="btn btn-outline-danger" type="button" onclick={() => doRotateKey()}>{$_('Confirm the rollover')}</button>
                </div>
            </div>
        </div>
        <div
            class="modal-backdrop"
            onclick={() => {
                keyTypeToRotate = null
            }}
            aria-hidden="true"
        ></div>
    </div>
{/if}

{#if $zoneStatus && keyToExpire}
    <div class="modal" role="dialog" transition:fade={{ duration: 250 }}>
        <div class="modal-dialog modal-lg">
            <div class="modal-content">
                <div class="modal-header text-bg-primary"><div>{$_('Expire a DNSSEC key')}</div></div>
                <div class="modal-body">
                    {#if keyToExpire.type == 'zsk'}
                        <p>{$_('Expiring a key will mark it to be deleted after some time, based on the TTL of the entries in the zone.')}</p>
                    {:else if $zoneStatus.ds_domain_linked}
                        <p>{$_('Expiring a key will mark it to be deleted in two days. The DS entries on your domain will be automatically updated to remove this key, and in 2 days, the zone will be resigned without this key. If this is the last key, DNSSEC will be disabled on your domain from that moment.')}</p>
                    {:else}
                        <p>{$_('Expiring a key will mark it to be deleted in two days. If your domain is not updated to remove the corresponding DS entry immediately, the zone might become rejected by resolvers validating DNSSEC.')}</p>
                    {/if}
                </div>
                <div class="modal-footer text-bg-light">
                    <button class="btn btn-outline-secondary" type="button" onclick={() => (keyToExpire = null)}>{$_('Cancel')}</button>
                    <button class="btn btn-outline-danger" type="button" onclick={() => doExpireKey()}>{$_('Expire the key {key_tag} / {alg}', { key_tag: keyToExpire.key_tag, alg: `${keyToExpire.alg}–${$algorithmIndex[keyToExpire.alg].text}` })}</button>
                </div>
            </div>
        </div>
        <div
            class="modal-backdrop"
            onclick={() => {
                keyToExpire = null
            }}
            aria-hidden="true"
        ></div>
    </div>
{/if}

{#if keyToUnexpire}
    <div class="modal" role="dialog" transition:fade={{ duration: 250 }}>
        <div class="modal-dialog modal-lg">
            <div class="modal-content">
                <div class="modal-header text-bg-primary"><div>{$_('Bring back a DNSSEC key')}</div></div>
                <div class="modal-body">
                    <p>{$_('You should only keep one key active at any given time to keep the size of the zone minimal.')}</p>
                </div>
                <div class="modal-footer text-bg-light">
                    <button class="btn btn-outline-secondary" type="button" onclick={() => (keyToUnexpire = null)}>{$_('Cancel')}</button>
                    <button class="btn btn-outline-danger" type="button" onclick={() => doUnexpireKey()}>{$_('Bring back the key {key_tag} / {alg}', { key_tag: keyToUnexpire.key_tag, alg: `${keyToUnexpire.alg}–${$algorithmIndex[keyToUnexpire.alg].text}` })}</button>
                </div>
            </div>
        </div>
        <div
            class="modal-backdrop"
            onclick={() => {
                keyToUnexpire = null
            }}
            aria-hidden="true"
        ></div>
    </div>
{/if}

{#if generateKeyModal}
    <div class="modal" role="dialog" transition:fade={{ duration: 250 }}>
        <div class="modal-dialog modal-lg">
            <div class="modal-content">
                <div class="modal-header text-bg-primary"><div>{$_('Generate a new DNSSEC key')}</div></div>

                <div class="modal-body dnssec-key-selection">
                    <div class="dks-table">
                        <div class="dks-row head">
                            <div class="dks-code">{$_('Code')}</div>
                            <div class="dks-name">{$_('Name')}</div>
                            <div class="dks-status">{$_('Status')}</div>
                            <div class="dks-action"></div>
                        </div>
                        {#each $algorithms.filter((alg) => allowedAlgorithms.has(alg.id)) as alg}
                            <div
                                onclick={(e) => {
                                    e.preventDefault()
                                    if (alg.id != newKeyAlgId) newKeyAlgId = alg.id
                                }}
                                aria-hidden="true"
                                class="dks-row"
                                class:selected={alg.id == newKeyAlgId}
                            >
                                <div class="dks-code">{alg.id}</div>
                                <div class="dks-name">{alg.text}</div>
                                <div class="dks-status">
                                    {#if alg.status == 'NOT RECOMMENDED'}
                                        <span class="s-not-recommended label label-warning">{$_('Not recommended')}</span>
                                    {:else if alg.status == 'MAY' || alg.status == 'MUST'}
                                        <span class="s-may label label-default">{$_('Allowed')}</span>
                                    {:else if alg.status == 'RECOMMENDED'}
                                        <stan class="s-recommended label label-success">{$_('Allowed')}</stan>
                                    {/if}
                                </div>
                                <div class="dks-action">
                                    <button
                                        onclick={(e) => {
                                            e.preventDefault()
                                            newKeyAlgId = alg.id
                                        }}
                                        class="btn btn-outline-secondary btn-sm"
                                        type="submit"
                                        disabled={newKeyAlgId == alg.id}>{$_('Select')}</button
                                    >
                                </div>
                            </div>
                        {/each}
                    </div>
                    {#if newKeyAlgId != null && $algorithmIndex[newKeyAlgId].extra_text != null && asAny($algorithmIndex[newKeyAlgId].extra_text)[$lg]}
                        <div class="dks-extra">
                            <p><strong>{$_('Extra information about “{alg}”', { alg: $algorithmIndex[newKeyAlgId].text })}</strong></p>
                            <div>{asAny($algorithmIndex[newKeyAlgId].extra_text)[$lg]}</div>
                        </div>
                    {/if}
                    {#if newKeyAlgId != null && generateKeyModal == 'ksk' && $zoneStatus}
                        <p><strong>{$_('Important')}</strong></p>
                        {#if $zoneStatus.ds_domain_linked}
                            <p>{$_('The DS entries will be automatically updated on your domain.')}</p>
                        {:else}
                            <p>{$_('You must update the new DS entries on your domain as soon as possible or have your domain rendered useless for a few days for all the client having DNS servers supporting DNSSEC.')}</p>
                        {/if}
                        <p>{$_('All the currently active keys will be set to expire after some time to allow the caches around the world to see the new keys.')}</p>
                    {/if}
                </div>

                <div class="modal-footer text-bg-light">
                    <button
                        class="btn btn-outline-secondary"
                        type="button"
                        onclick={() => {
                            generateKeyModal = null
                            newKeyAlgId = null
                        }}>{$_('Cancel')}</button
                    >
                    <button class="btn btn-primary" type="button" onclick={() => generateKey()} disabled={newKeyAlgId == null}>{$_('Generate')}</button>
                </div>
            </div>
        </div>
        <div
            class="modal-backdrop"
            onclick={() => {
                generateKeyModal = null
                newKeyAlgId = null
            }}
            aria-hidden="true"
        ></div>
    </div>
{/if}
